import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useEffect, useState } from 'react'
import type { GoodAllocationListItem } from '../../types/api'
import { useLazyPrintGoodAllocationBbgnQuery } from '../../store/api/procurementApi'
import { printAndUploadReport } from '../../utils/printAndUploadReport'
import { buildPrintPreviewHtml, sanitizePdfFileNamePart } from '../../utils/printPreview'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '../../theme/responsive'

// Access port: rpProcurementBBGN ("BIÊN BẢN GIAO NHẬN" = goods handover minutes)
// for a Goods Allocation row. Self-fetching dialog: on open it lazy-fires the
// BBGN print query with `row.id`, renders the returned `html` into an iframe
// srcDoc preview + a Print button. Mirrors ProcurementLiquidPrintDialog.

export interface GoodAllocationBbgnPrintDialogProps {
  open: boolean
  row: GoodAllocationListItem | null
  onClose: () => void
}

export default function GoodAllocationBbgnPrintDialog({
  open,
  row,
  onClose,
}: GoodAllocationBbgnPrintDialogProps) {
  const [toastMessage, setToastMessage] = useState('')
  const [trigger, { currentData, isFetching, isError, error, originalArgs }] =
    useLazyPrintGoodAllocationBbgnQuery()

  const id = row?.id ?? null

  useEffect(() => {
    if (open && id != null) {
      void trigger(id)
    }
  }, [open, id, trigger])

  // Chỉ show preview khi ĐÃ load xong html của ĐÚNG row đang mở:
  //  - `currentData` (khác `data`) tự về undefined khi đổi arg → không dính html cũ
  //  - chặn `isFetching` + khớp `originalArgs === id` để tránh render sớm.
  // → Dialog chỉ mở sau khi data về; trong lúc load hiện loading overlay.
  const isCurrent = !isFetching && originalArgs === id
  const rawHtml = isCurrent ? currentData?.html ?? '' : ''
  const previewHtml = rawHtml ? buildPrintPreviewHtml(rawHtml, { styleId: 'bbgn-print-preview-overrides' }) : ''
  const truckNo = row?.m_truck_no ?? ''
  const iframeTitle = 'BBGN Print Preview'

  // Surface BE ValueError ("This receipt is not BBGN!") as a toast.
  useEffect(() => {
    if (open && isError) {
      const detail =
        (error as { data?: { detail?: string } } | undefined)?.data?.detail ??
        'Cannot load BBGN.'
      setToastMessage(detail)
    }
  }, [open, isError, error])

  return (
    <>
      {open && id != null && (isFetching || !isCurrent) && !isError && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog
        open={open && Boolean(rawHtml)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}
        >
          <Box
            component="span"
            title={`Print Preview - BBGN (${truckNo})`}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
              Print Preview -{' '}
            </Box>
            BBGN ({truckNo})
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!rawHtml}
              onClick={() => {
                // Tên file BBGN = "<contract_no>_<truckNo>.pdf".
                const bbgnName = [(row?.contract_no ?? '').trim(), truckNo.trim()].filter(Boolean).join('_')
                const printTitle = `${sanitizePdfFileNamePart(bbgnName || 'procurement_bbgn')}.pdf`
                void printAndUploadReport({
                  iframeTitle,
                  reportName: 'rpProcurementBBGN',
                  printTitle,
                  html: previewHtml,
                  onUploadError: setToastMessage,
                })
              }}
            >
              Print
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
